import type { VisibleCardViewV1 } from "@fate-kingdom/engine";
import { CanvasTexture, LinearFilter, SRGBColorSpace, type Texture } from "three";

import { getAlphaCardPresentation } from "../alpha/presentation/alpha-card-presentation";

export interface PhysicalCardFaceSpec {
  readonly key: string;
  readonly name: string;
  readonly typeLabel: string;
  readonly faction: "assault" | "balanced" | "neutral";
  readonly artUrl?: string | undefined;
  readonly subtitle: string;
  readonly stats: readonly string[];
  readonly hidden: boolean;
}

export interface DisposableTexture {
  dispose(): void;
}

interface TextureEntry<TTexture extends DisposableTexture> {
  readonly texture: TTexture;
  references: number;
}

export class CardFaceTextureCache<TTexture extends DisposableTexture> {
  readonly #entries = new Map<string, TextureEntry<TTexture>>();
  readonly #factory: (spec: PhysicalCardFaceSpec) => TTexture;

  constructor(factory: (spec: PhysicalCardFaceSpec) => TTexture) {
    this.#factory = factory;
  }

  get(spec: PhysicalCardFaceSpec): TTexture {
    const existing = this.#entries.get(spec.key);
    if (existing !== undefined) return existing.texture;
    const texture = this.#factory(spec);
    this.#entries.set(spec.key, { texture, references: 0 });
    return texture;
  }

  retain(key: string): void {
    const entry = this.#entries.get(key);
    if (entry !== undefined) entry.references += 1;
  }

  release(key: string): void {
    const entry = this.#entries.get(key);
    if (entry !== undefined) entry.references = Math.max(0, entry.references - 1);
  }

  disposeUnretained(): void {
    for (const [key, entry] of this.#entries) {
      if (entry.references !== 0) continue;
      entry.texture.dispose();
      this.#entries.delete(key);
    }
  }

  clear(): void {
    for (const entry of this.#entries.values()) entry.texture.dispose();
    this.#entries.clear();
  }

  get size(): number {
    return this.#entries.size;
  }
}

const FACTION_COLORS = {
  assault: { deep: "#461b29", accent: "#cd6470" },
  balanced: { deep: "#17364b", accent: "#67aed7" },
  neutral: { deep: "#28383b", accent: "#d2b86d" },
} as const;

function roundedPath(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + radius);
  context.lineTo(x + width, y + height - radius);
  context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  context.lineTo(x + radius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.closePath();
}

function fitText(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  initialSize: number,
  minimumSize: number,
): number {
  let size = initialSize;
  while (size > minimumSize) {
    context.font = `700 ${String(size)}px Georgia, serif`;
    if (context.measureText(text).width <= maxWidth) break;
    size -= 2;
  }
  return size;
}

function drawCardFace(
  context: CanvasRenderingContext2D,
  spec: PhysicalCardFaceSpec,
  art?: CanvasImageSource,
): void {
  const { width, height } = context.canvas;
  const colors = FACTION_COLORS[spec.faction];
  context.clearRect(0, 0, width, height);

  const background = context.createLinearGradient(0, 0, width, height);
  background.addColorStop(0, "#f4edda");
  background.addColorStop(0.72, "#cdbb8a");
  background.addColorStop(1, "#897144");
  context.fillStyle = background;
  context.fillRect(0, 0, width, height);

  context.fillStyle = colors.deep;
  context.fillRect(18, 18, width - 36, height - 36);
  context.strokeStyle = colors.accent;
  context.lineWidth = 7;
  context.strokeRect(25, 25, width - 50, height - 50);

  if (spec.hidden) {
    const back = context.createRadialGradient(
      width / 2,
      height / 2,
      20,
      width / 2,
      height / 2,
      280,
    );
    back.addColorStop(0, "#315a61");
    back.addColorStop(0.54, "#142e3a");
    back.addColorStop(1, "#07161f");
    context.fillStyle = back;
    context.fillRect(40, 40, width - 80, height - 80);
    context.strokeStyle = "#d5b966";
    context.lineWidth = 8;
    context.beginPath();
    context.arc(width / 2, height / 2, 112, 0, Math.PI * 2);
    context.stroke();
    context.beginPath();
    context.moveTo(width / 2, height / 2 - 86);
    context.lineTo(width / 2 + 76, height / 2 + 52);
    context.lineTo(width / 2 - 76, height / 2 + 52);
    context.closePath();
    context.stroke();
    context.fillStyle = "#f0d88b";
    context.font = "700 38px Georgia, serif";
    context.textAlign = "center";
    context.fillText("FATE", width / 2, height / 2 + 148);
    context.font = "700 27px Georgia, serif";
    context.fillText("KINGDOM", width / 2, height / 2 + 184);
    return;
  }

  context.fillStyle = colors.accent;
  context.fillRect(32, 32, width - 64, 58);
  context.fillStyle = "#f8f0d5";
  context.font = "700 25px system-ui, sans-serif";
  context.textAlign = "left";
  context.fillText(spec.typeLabel.toUpperCase(), 50, 70);

  const titleSize = fitText(context, spec.name, width - 96, 38, 24);
  context.font = `700 ${String(titleSize)}px Georgia, serif`;
  context.fillStyle = "#f9edca";
  context.fillText(spec.name, 48, 132);

  roundedPath(context, 42, 158, width - 84, 340, 18);
  context.save();
  context.clip();
  if (art === undefined) {
    const artFill = context.createLinearGradient(42, 158, width - 42, 498);
    artFill.addColorStop(0, colors.deep);
    artFill.addColorStop(1, "#0d1f27");
    context.fillStyle = artFill;
    context.fillRect(42, 158, width - 84, 340);
    context.strokeStyle = `${colors.accent}aa`;
    context.lineWidth = 4;
    for (let index = -2; index < 8; index += 1) {
      context.beginPath();
      context.moveTo(42 + index * 96, 498);
      context.lineTo(180 + index * 96, 158);
      context.stroke();
    }
  } else {
    context.drawImage(art, 42, 158, width - 84, 340);
  }
  const shade = context.createLinearGradient(0, 280, 0, 500);
  shade.addColorStop(0, "rgba(3, 12, 17, 0)");
  shade.addColorStop(1, "rgba(3, 12, 17, 0.62)");
  context.fillStyle = shade;
  context.fillRect(42, 158, width - 84, 340);
  context.restore();

  context.strokeStyle = "#e7d493";
  context.lineWidth = 4;
  roundedPath(context, 42, 158, width - 84, 340, 18);
  context.stroke();

  context.fillStyle = "#f8e8b4";
  context.font = "600 27px system-ui, sans-serif";
  context.fillText(spec.subtitle, 48, 548);
  context.fillStyle = "#fff4cf";
  context.font = "700 31px system-ui, sans-serif";
  context.fillText(spec.stats.join("   "), 48, 612);
  context.fillStyle = "#cbb67c";
  context.font = "600 20px system-ui, sans-serif";
  context.fillText("LUMINOUS IMPERIAL WAR TABLE", 48, 674);
}

export function createPhysicalCardFaceSpec(
  card: VisibleCardViewV1,
  options: {
    readonly subtitle?: string | undefined;
    readonly stats?: readonly string[] | undefined;
  } = {},
): PhysicalCardFaceSpec {
  const presentation = getAlphaCardPresentation(card);
  const defaultStats =
    card.type === "land"
      ? [`CONTROL ${String(card.stages[0].maximumControl)}`]
      : card.type === "commander"
        ? [`ATK ${String(card.attack ?? 0)}`, `GRD ${String(card.guard ?? 0)}`]
        : card.type === "army"
          ? [
              `ATK ${String(card.attack)}`,
              `GRD ${String(card.guard)}`,
              `SIZE ${String(card.armySize)}`,
            ]
          : [];
  const stats = options.stats ?? defaultStats;
  const subtitle = options.subtitle ?? presentation.shortLabel;
  return {
    key: ["face-v1", card.cardDefinitionId, presentation.faction, subtitle, stats.join(":")].join(
      "|",
    ),
    name: presentation.displayName,
    typeLabel: card.type,
    faction: presentation.faction,
    artUrl: presentation.artUrl,
    subtitle,
    stats,
    hidden: false,
  };
}

export function hiddenPhysicalCardFaceSpec(): PhysicalCardFaceSpec {
  return {
    key: "fate-kingdom-card-back-v1",
    name: "Hidden card",
    typeLabel: "hidden",
    faction: "neutral",
    subtitle: "",
    stats: [],
    hidden: true,
  };
}

function createTexture(spec: PhysicalCardFaceSpec): Texture {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 716;
  const context = canvas.getContext("2d");
  if (context === null) throw new Error("Unable to create the physical card face canvas.");
  drawCardFace(context, spec);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.generateMipmaps = false;

  if (!spec.hidden && spec.artUrl !== undefined) {
    const image = new Image();
    let active = true;
    texture.addEventListener("dispose", () => {
      active = false;
    });
    image.decoding = "async";
    image.onload = () => {
      if (!active) return;
      drawCardFace(context, spec, image);
      texture.needsUpdate = true;
    };
    image.src = spec.artUrl;
  }

  return texture;
}

export const physicalCardFaceTextureCache = new CardFaceTextureCache<Texture>(createTexture);
